/**
 * TemporalSeriesView Component
 *
 * Displays a temporal series with a visual sparkline chart and compact value display.
 * Shows the trajectory over time with the current year highlighted.
 */

import React, { useState } from 'react';
import { useGraph } from '../GraphContext';
import { ComputedNode, SeriesMeta } from '../../types';
import { temporalId, getSeriesPatternDescription } from '../../temporal';

export interface TemporalSeriesViewProps {
  seriesMeta: SeriesMeta;
  currentYear: number;
  onYearClick?: (nodeId: string) => void;
}

export function TemporalSeriesView({
  seriesMeta,
  currentYear,
  onYearClick,
}: TemporalSeriesViewProps): React.ReactElement {
  const ctx = useGraph();
  const { seriesId, pattern, totalYears, seriesLabel } = seriesMeta;
  const [showAllYears, setShowAllYears] = useState(false);

  // Get all year values
  const yearValues: number[] = [];
  for (let year = 0; year < totalYears; year++) {
    const nodeId = temporalId(seriesId, year);
    const value = ctx.getValue(nodeId);
    yearValues.push(value ?? 0);
  }

  // Get unit from first node
  const firstNode = ctx.getNode(temporalId(seriesId, 0)) as ComputedNode | undefined;
  const unit = firstNode?.unit || 'number';

  // Calculate stats
  const minVal = Math.min(...yearValues);
  const maxVal = Math.max(...yearValues);
  const range = maxVal - minVal || 1;
  const year0 = yearValues[0];
  const yearFinal = yearValues[totalYears - 1];
  const growth = year0 !== 0 ? ((yearFinal / year0) - 1) * 100 : 0;

  return (
    <div className="border-b border-gray-100 p-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">
            {seriesLabel} Series
          </div>
          <div className="text-xs text-gray-400 italic mt-0.5">
            {getSeriesPatternDescription(pattern)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-gray-900">
            {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500">total growth</div>
        </div>
      </div>

      {/* Sparkline chart */}
      <div className="h-16 flex items-end gap-0.5 mb-3 bg-gray-50 p-2 rounded">
        {yearValues.map((val, idx) => {
          const height = ((val - minVal) / range) * 100;
          const isCurrent = idx === currentYear;
          const nodeId = temporalId(seriesId, idx);
          return (
            <button
              key={idx}
              onClick={() => onYearClick?.(nodeId)}
              className={`
                flex-1 min-w-0 transition-all rounded-t
                ${isCurrent
                  ? 'bg-gray-800'
                  : 'bg-gray-300 hover:bg-gray-400'}
              `}
              style={{ height: `${Math.max(8, height)}%` }}
              title={`Year ${idx}: ${ctx.formatValue(val, unit)}`}
            />
          );
        })}
      </div>

      {/* Key values */}
      <div className="flex justify-between text-sm mb-3">
        <button
          onClick={() => onYearClick?.(temporalId(seriesId, 0))}
          className={`text-left px-2 py-1 rounded ${currentYear === 0 ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
        >
          <div className="text-xs text-gray-500">Year 0</div>
          <div className="font-medium text-gray-900">{ctx.formatValue(year0, unit)}</div>
        </button>
        <button
          onClick={() => onYearClick?.(temporalId(seriesId, currentYear))}
          className="text-center px-2 py-1 rounded bg-gray-100"
        >
          <div className="text-xs text-gray-500">Year {currentYear}</div>
          <div className="font-medium text-gray-900">{ctx.formatValue(yearValues[currentYear], unit)}</div>
        </button>
        <button
          onClick={() => onYearClick?.(temporalId(seriesId, totalYears - 1))}
          className={`text-right px-2 py-1 rounded ${currentYear === totalYears - 1 ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
        >
          <div className="text-xs text-gray-500">Year {totalYears - 1}</div>
          <div className="font-medium text-gray-900">{ctx.formatValue(yearFinal, unit)}</div>
        </button>
      </div>

      {/* Expandable full table */}
      <button
        onClick={() => setShowAllYears(!showAllYears)}
        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
      >
        <svg
          className={`w-3 h-3 transition-transform ${showAllYears ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {showAllYears ? 'Hide' : 'Show'} all {totalYears} years
      </button>

      {showAllYears && (
        <div className="mt-2 grid grid-cols-3 gap-1 text-xs">
          {yearValues.map((val, idx) => {
            const isCurrent = idx === currentYear;
            const nodeId = temporalId(seriesId, idx);
            return (
              <button
                key={idx}
                onClick={() => onYearClick?.(nodeId)}
                className={`
                  text-left px-2 py-1 rounded
                  ${isCurrent ? 'bg-gray-200 font-medium' : 'hover:bg-gray-50'}
                `}
              >
                <span className="text-gray-500">Yr {idx}:</span>{' '}
                <span className="text-gray-800">{ctx.formatValue(val, unit)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TemporalSeriesView;
